import { useState, useRef, useCallback, useEffect } from 'react';
import { SEO } from "@/components/seo/SEO";
import { Navigation } from '@/components/layout/Navigation';
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { PageContainer } from '@/components/layout/PageContainer';
import { useFreeUsage } from '@/hooks/useFreeUsage';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { useClientRecords } from '@/hooks/useClientRecords';
import { supabase } from '@/integrations/supabase/client';
import { CaseConversation } from '@/components/answer/CaseConversation';
import { withAnonHeader, applyRefreshFromBody, clearAnonToken } from '@/lib/anonIdentity';
import {
  ArrowLeft, Upload, X, Scissors, Sparkles, AlertCircle, Camera,
  Palette, Shield, Mic, MicOff, BookmarkPlus, Check, ChevronRight, MessageCircle, History,
} from 'lucide-react';
import { AskHistorySidebar, AskHistoryDrawer, type HistoryItem } from '@/components/ask/AskHistorySidebar';
import { PRIMEIntakeWidget, type PRIMECanvas } from '@/components/ask/PRIMEIntakeWidget';
import { CameraCaptureModal, probeCameraAvailable } from '@/components/media/CameraCaptureModal';
import {
  trackAskPageVisit,
  trackFirstQuestionSubmitted,
  trackSecondQuestionSubmitted,
} from '@/lib/analytics';
import { AnswerFeedback } from '@/components/answer/AnswerFeedback';
import { ChallengeTheAI } from '@/components/answer/ChallengeTheAI';
import { captureUpgradeIntent, markAnswerSeen } from '@/lib/intentDetection';
import { ColorNeutralizingChart } from '@/components/color/ColorNeutralizingChart';
import { useAdmin } from '@/hooks/useAdmin';


// ─── Types ────────────────────────────────────────────────────────────────────

type ServiceType = 'cut' | 'color' | 'correction';
type UserServiceSelection = 'HAIRCUT' | 'COLOR' | 'HAIR_RESCUE' | null;
type Step = 'intake' | 'analyzing' | 'results' | 'error';
type ErrorKind = 'paywall' | 'retryable' | 'failed';

interface HairPlan {
  serviceType: ServiceType;
  cr: string;
  dr: string;
  ha?: string;
  fa?: string;
  section?: string;
  steps?: string[];
  blending?: string;
  liftGoal?: string;
  formula?: string;
  developer?: string;
  applicationPlacement?: string;
  processingGoal?: string;
  toner?: string;
  expectedResult: string;
  expectedResultColor?: string;
  stylistScript?: string;
  // Cutting-plan extended fields
  haircutGoal?: string;
  lengthGuide?: string;
  cuttingTechnique?: string;
  faceFraming?: string;
  stylingFinish?: string;
  warningNotes?: string;
  // Raw text fallback so we never render an empty card
  rawText?: string;
}

const PRO_MESSAGE =
  "You've used your free questions. Unlock Pro Access to continue using Hair Pro Encyclopedia.ai — including client records, quizzes, certifications, and advanced diagnostics.";

const ANALYSIS_TIMEOUT_MS = 60_000;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

async function sha256HexBrowser(input: string): Promise<string> {
  const bytes = new TextEncoder().encode(input);
  const hash = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(hash)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

function parseAIResponse(raw: string, crFallback: string, service: UserServiceSelection): HairPlan {
  const section = (heading: string) => {
    const rx = new RegExp(`\\*\\*${heading}[^*]*\\*\\*\\s*\\n?([^\\n*][\\s\\S]*?)(?=\\n\\*\\*|\\n---\\s*\\n|$)`, 'i');
    return raw.match(rx)?.[1]?.trim() ?? '';
  };

  const hasBothPlans = /---/.test(raw);
  const serviceType: ServiceType =
    service === 'HAIR_RESCUE' ? 'correction'
    : service === 'COLOR' ? 'color'
    : service === 'HAIRCUT' ? 'cut'
    : hasBothPlans ? 'correction' : /COLOR PLAN/i.test(raw) ? 'color' : 'cut';

  const steps: string[] = [];
  const stepsMatch = raw.match(/\*\*Steps\*\*\s*\n([\s\S]*?)(?=\n\*\*|$)/i);
  if (stepsMatch) {
    stepsMatch[1].split('\n').forEach((l) => {
      const m = l.match(/^\d+\.\s+(.+)/);
      if (m) steps.push(m[1].trim());
    });
  }

  const sectioning = section('Sectioning') || raw.match(/\bSection:\s*([^\n]+)/i)?.[1]?.trim();

  return {
    serviceType,
    cr: section('CR[^*]*') || crFallback,
    dr: section('DR[^*]*'),
    ha: raw.match(/\bHA:\s*([^\n]+)/i)?.[1]?.trim(),
    fa: raw.match(/\bFA:\s*([^\n]+)/i)?.[1]?.trim(),
    section: sectioning,
    steps,
    blending: section('Blending'),
    liftGoal: section('Lift Goal'),
    formula: section('Formula'),
    developer: section('Developer'),
    applicationPlacement: section('Application[^*]*'),
    processingGoal: section('Processing Goal'),
    toner: section('Toner'),
    expectedResult: section('Result'),
    expectedResultColor: serviceType !== 'cut' ? section('Result') : undefined,
    stylistScript: section('Stylist Script'),
    haircutGoal: section('Haircut Goal'),
    lengthGuide: section('Length Guide'),
    cuttingTechnique: section('Cutting Technique'),
    faceFraming: section('Face[- ]?Framing'),
    stylingFinish: section('Styling Finish'),
    warningNotes: section('Professional Warning Notes') || section('Warning'),
    rawText: raw,
  };
}

// ─── Result Card Components ───────────────────────────────────────────────────

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-muted/40 border border-border/60 px-3.5 py-3">
      <p className="font-body text-[11px] font-semibold uppercase tracking-widest text-muted-foreground mb-1">{label}</p>
      <p className="font-body text-sm text-foreground leading-relaxed whitespace-pre-line">{value}</p>
    </div>
  );
}

function RawFallback({ text }: { text: string }) {
  const cleaned = text.replace(/\*\*/g, '').replace(/^---+\s*$/gm, '').trim();
  return (
    <div className="rounded-xl bg-muted/40 border border-border/60 px-3.5 py-3">
      <p className="font-body text-sm text-foreground leading-relaxed whitespace-pre-line">{cleaned}</p>
    </div>
  );
}

function CuttingPlanCard({ plan, question }: { plan: HairPlan; question?: string }) {
  const hasStructured = !!(
    plan.haircutGoal || plan.section || plan.lengthGuide || plan.ha || plan.fa ||
    plan.cuttingTechnique || plan.faceFraming || plan.blending || plan.stylingFinish ||
    plan.warningNotes || (plan.steps && plan.steps.length > 0)
  );

  return (

    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}
      className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 bg-muted/60 border-b border-border">
        <Scissors className="h-4 w-4 text-foreground" />
        <span className="font-body text-xs font-semibold uppercase tracking-widest text-foreground">Cutting Plan</span>
      </div>
      <div className="px-4 py-4 space-y-3">
        {plan.cr && <InfoRow label="CR — Current Reality" value={plan.cr} />}
        {plan.dr && <InfoRow label="DR — Desired Reality" value={plan.dr} />}

        {!hasStructured && plan.rawText && <RawFallback text={plan.rawText} />}

        {plan.haircutGoal && <InfoRow label="Haircut Goal" value={plan.haircutGoal} />}
        {plan.section && <InfoRow label="Sectioning" value={plan.section} />}
        {plan.lengthGuide && <InfoRow label="Length Guide" value={plan.lengthGuide} />}

        {(plan.ha || plan.fa) && (
          <div className="space-y-1.5">
            <p className="font-body text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">Elevation</p>
            <div className="grid grid-cols-2 gap-2">
              {[{ label: 'HA', value: plan.ha || '—' }, { label: 'FA', value: plan.fa || '—' }].map(({ label, value }) => (
                <div key={label} className="rounded-xl bg-background border border-border px-3 py-2.5 text-center">
                  <p className="font-body text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{label}</p>
                  <p className="font-body text-sm font-semibold text-foreground mt-0.5 leading-tight">{value}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {plan.cuttingTechnique && <InfoRow label="Cutting Technique" value={plan.cuttingTechnique} />}
        {plan.faceFraming && <InfoRow label="Face-Framing" value={plan.faceFraming} />}

        {plan.steps && plan.steps.length > 0 && (
          <div className="space-y-1.5">
            <p className="font-body text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">Steps</p>
            {plan.steps.map((step, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-foreground font-body text-[11px] font-semibold text-primary-foreground">{i + 1}</span>
                <p className="font-body text-sm text-foreground leading-relaxed">{step}</p>
              </div>
            ))}
          </div>
        )}

        {plan.blending && <InfoRow label="Blending" value={plan.blending} />}
        {plan.stylingFinish && <InfoRow label="Styling Finish" value={plan.stylingFinish} />}

        {plan.warningNotes && (
          <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-3.5 py-3">
            <p className="font-body text-[11px] font-semibold uppercase tracking-widest text-destructive mb-1">Professional Warning Notes</p>
            <p className="font-body text-sm text-foreground leading-relaxed whitespace-pre-line">{plan.warningNotes}</p>
          </div>
        )}

        {plan.expectedResult && (
          <div className="flex items-start gap-2">
            <Sparkles className="h-4 w-4 shrink-0 mt-0.5 text-foreground" />
            <p className="font-body text-sm text-foreground italic leading-relaxed">{plan.expectedResult}</p>
          </div>
        )}
        {plan.stylistScript && (
          <div className="space-y-3">
            <div className="rounded-xl bg-accent/30 border border-border/60 px-3.5 py-3">
              <p className="font-body text-[11px] font-semibold uppercase tracking-widest text-muted-foreground mb-1">Stylist Script</p>
              <p className="font-body text-sm text-foreground leading-relaxed italic">"{plan.stylistScript}"</p>
            </div>
            <ChallengeTheAI question={question} answerContext={plan} />
          </div>
        )}
      </div>
    </motion.div>
  );
}


function ColorPlanCard({ plan, question }: { plan: HairPlan; question?: string }) {
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}
      className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden">

      <div className="flex items-center gap-2 px-4 py-2.5 bg-muted/60 border-b border-border">
        <Palette className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="font-body text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">Color Plan</span>
      </div>
      <div className="px-4 py-4 space-y-3">
        {plan.cr && <InfoRow label="CR — Current Reality" value={plan.cr} />}
        {plan.dr && <InfoRow label="DR — Desired Reality" value={plan.dr} />}
        {plan.liftGoal && <InfoRow label="Lift Goal" value={plan.liftGoal} />}
        {plan.formula && <InfoRow label="Formula" value={plan.formula} />}
        {plan.developer && <InfoRow label="Developer" value={plan.developer} />}
        {plan.applicationPlacement && <InfoRow label="Application / Placement" value={plan.applicationPlacement} />}
        {plan.processingGoal && <InfoRow label="Processing Goal" value={plan.processingGoal} />}
        {plan.toner && <InfoRow label="Toner" value={plan.toner} />}
        {plan.expectedResult && (
          <div className="flex items-start gap-2">
            <Sparkles className="h-3.5 w-3.5 shrink-0 mt-0.5 text-muted-foreground" />
            <p className="font-body text-xs text-muted-foreground italic leading-relaxed">{plan.expectedResultColor ?? plan.expectedResult}</p>
          </div>
        )}
        {plan.stylistScript && (
          <div className="space-y-3">
            <div className="rounded-xl bg-accent/30 border border-border/60 px-3 py-2.5">
              <p className="font-body text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1">Stylist Script</p>
              <p className="font-body text-xs text-foreground leading-relaxed italic">"{plan.stylistScript}"</p>
            </div>
            <ChallengeTheAI question={question} answerContext={plan} />
          </div>
        )}
        <ColorNeutralizingChart

          context={[plan.formula, plan.toner, plan.expectedResultColor ?? plan.expectedResult, plan.cr, plan.dr].filter(Boolean).join(' ')}
        />
      </div>
    </motion.div>
  );
}

function PlanCard({ plan, question }: { plan: HairPlan; question?: string }) {
  if (plan.serviceType === 'color') return <ColorPlanCard plan={plan} question={question} />;
  if (plan.serviceType === 'correction') return (
    <div className="space-y-4"><ColorPlanCard plan={plan} question={question} /><CuttingPlanCard plan={plan} question={question} /></div>
  );
  return <CuttingPlanCard plan={plan} question={question} />;
}


// ─── Section Label ────────────────────────────────────────────────────────────

const SectionLabel = React.forwardRef<HTMLDivElement, { number: string; title: string }>(
  ({ number, title }, ref) => (
    <div ref={ref} className="flex items-center gap-3 mb-3">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-foreground font-body text-[10px] font-bold text-primary-foreground">
        {number}
      </span>
      <p className="font-display text-base text-foreground tracking-tight">{title}</p>
    </div>
  )
);
SectionLabel.displayName = 'SectionLabel';

// ─── Photo Upload Section ─────────────────────────────────────────────────────

interface PhotoSectionProps {
  sectionNum: string;
  title: string;
  uploadLabel: string;
  helperText: string;
  accent?: boolean;
  preview: string | null;
  onSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClear: () => void;
  inputRef: React.RefObject<HTMLInputElement>;
}

function PhotoSection({ sectionNum, title, uploadLabel, helperText, accent, preview, onSelect, onClear, inputRef }: PhotoSectionProps) {
  const cameraFallbackRef = useRef<HTMLInputElement>(null);
  const [cameraOpen, setCameraOpen] = useState(false);

  const handleTakePhoto = async () => {
    const ok = await probeCameraAvailable();
    if (ok) {
      setCameraOpen(true);
    } else {
      cameraFallbackRef.current?.click();
    }
  };

  const handleCameraCapture = (file: File) => {
    const dt = new DataTransfer();
    dt.items.add(file);
    const synthetic = {
      target: { files: dt.files },
      currentTarget: { files: dt.files },
    } as unknown as React.ChangeEvent<HTMLInputElement>;
    onSelect(synthetic);
  };

  return (
    <div className={`rounded-2xl border ${accent ? 'border-primary/20 bg-primary/[0.03]' : 'border-border bg-card'} p-5`}>
      <SectionLabel number={sectionNum} title={title} />

      {/* Hidden file input — gallery / library upload */}
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onSelect} />
      {/* Hidden file input — camera fallback (mobile capture attr) */}
      <input ref={cameraFallbackRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onSelect} />

      {preview ? (
        <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
          className="relative rounded-xl overflow-hidden border border-border shadow-sm mb-3">
          <img src={preview} alt={title} className="w-full max-h-48 object-cover"  loading="lazy" decoding="async"/>
          <button onClick={onClear}
            className="absolute top-2.5 right-2.5 flex h-7 w-7 items-center justify-center rounded-full bg-foreground/85 text-primary-foreground shadow backdrop-blur-sm transition-opacity hover:opacity-80">
            <X className="h-3.5 w-3.5" />
          </button>
        </motion.div>
      ) : (
        <div className="flex gap-3 mb-3">
          <motion.button
            whileHover={{ y: -1 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleTakePhoto}
            className="flex-1 rounded-xl border-2 border-dashed border-border/80 bg-background py-6 flex flex-col items-center gap-2.5 transition-colors hover:border-primary/30 hover:bg-accent/20"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted border border-border/60">
              <Camera className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="font-body text-xs font-medium text-foreground">Take Photo</p>
          </motion.button>

          <motion.button
            whileHover={{ y: -1 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => inputRef.current?.click()}
            className="flex-1 rounded-xl border-2 border-dashed border-border/80 bg-background py-6 flex flex-col items-center gap-2.5 transition-colors hover:border-primary/30 hover:bg-accent/20"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted border border-border/60">
              <Upload className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="font-body text-xs font-medium text-foreground">Upload</p>
          </motion.button>
        </div>
      )}

      <p className="font-body text-[11px] text-muted-foreground leading-relaxed pl-0.5">{helperText}</p>

      <CameraCaptureModal
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCapture={handleCameraCapture}
      />
    </div>
  );
}

// ─── Service Type Selector ────────────────────────────────────────────────────

const SERVICE_OPTIONS = [
  { id: 'HAIRCUT' as UserServiceSelection, label: 'Haircut', icon: <Scissors className="h-3.5 w-3.5" />, description: 'Cutting plan' },
  { id: 'COLOR' as UserServiceSelection, label: 'Color', icon: <Palette className="h-3.5 w-3.5" />, description: 'Color plan' },
  { id: 'HAIR_RESCUE' as UserServiceSelection, label: 'Hair Rescue', icon: <Shield className="h-3.5 w-3.5" />, description: 'Color + cut' },
];

function ServiceTypeSelector({ selected, onChange }: { selected: UserServiceSelection; onChange: (v: UserServiceSelection) => void }) {
  const hasSelection = selected !== null;
  return (
    <div className="flex gap-2 mb-5 items-center justify-center">
      {SERVICE_OPTIONS.map((opt) => {
        const isActive = selected === opt.id;
        const isInactive = hasSelection && !isActive;
        return (
          <motion.button
            key={opt.id}
            onClick={() => onChange(isActive ? null : opt.id)}
            animate={{
              scale: isActive ? 1.08 : isInactive ? 0.93 : 1,
              opacity: isInactive ? 0.45 : 1,
              zIndex: isActive ? 10 : 1,
            }}
            transition={{ type: 'spring', stiffness: 320, damping: 28 }}
            whileTap={{ scale: isActive ? 1.04 : 0.9 }}
            className={`flex flex-col items-center gap-1.5 rounded-xl border px-2 py-3 transition-colors origin-center w-[30%] ${
              isActive
                ? 'border-primary bg-primary text-primary-foreground shadow-elevated'
                : 'border-border bg-card text-muted-foreground hover:border-primary/40 hover:bg-accent/30'
            }`}
          >
            {opt.icon}
            <span className="font-body text-[10px] font-semibold uppercase tracking-wide leading-tight">{opt.label}</span>
            <span className={`font-body text-[9px] leading-tight ${isActive ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>{opt.description}</span>
          </motion.button>
        );
      })}
    </div>
  );
}

// ─── Voice Button ─────────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnySpeechRecognition = any;

function VoiceSection({ onTranscript }: { onTranscript: (text: string) => void }) {
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef<AnySpeechRecognition>(null);

  const toggle = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w = window as any;
    const SR = w.SpeechRecognition ?? w.webkitSpeechRecognition;

    if (!SR) {
      alert('Voice input is not supported in this browser. Please use Chrome or Safari.');
      return;
    }

    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
      return;
    }

    const recognition = new SR() as AnySpeechRecognition;
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onresult = (e: AnySpeechRecognition) => {
      const transcript = e.results[0][0].transcript as string;
      onTranscript(transcript);
      setListening(false);
    };
    recognition.onerror = () => setListening(false);
    recognition.onend = () => setListening(false);
    recognition.start();
    recognitionRef.current = recognition;
    setListening(true);
  }, [listening, onTranscript]);

  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <SectionLabel number="4" title="Speak Instead" />

      <motion.button
        onClick={toggle}
        whileHover={{ y: -1 }}
        whileTap={{ scale: 0.97 }}
        animate={listening ? { boxShadow: ['0 0 0 0px hsl(12 76% 61% / 0)', '0 0 0 6px hsl(12 76% 61% / 0.15)', '0 0 0 0px hsl(12 76% 61% / 0)'] } : {}}
        transition={listening ? { repeat: Infinity, duration: 1.4 } : {}}
        className={`w-full flex items-center gap-4 rounded-xl border px-4 py-3.5 transition-colors ${
          listening
            ? 'border-destructive/50 bg-destructive/8 text-destructive'
            : 'border-border bg-background text-foreground hover:border-primary/40 hover:bg-accent/20'
        }`}
      >
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border transition-colors ${
          listening ? 'border-destructive/40 bg-destructive/10' : 'border-border bg-muted'
        }`}>
          {listening
            ? <MicOff className="h-4 w-4 text-destructive" />
            : <Mic className="h-4 w-4 text-muted-foreground" />
          }
        </div>
        <div className="text-left">
          <p className="font-body text-sm font-medium">
            {listening ? 'Listening… tap to stop' : 'Speak instead'}
          </p>
          <p className="font-body text-[11px] text-muted-foreground mt-0.5">
            {listening ? 'Recording your situation now' : 'Tap the mic and describe the situation verbally.'}
          </p>
        </div>
        {listening && (
          <div className="ml-auto flex gap-0.5">
            {[0, 1, 2].map((i) => (
              <motion.span key={i} className="block w-1 rounded-full bg-destructive/60"
                animate={{ height: ['8px', '18px', '8px'] }}
                transition={{ duration: 0.5, repeat: Infinity, delay: i * 0.15 }} />
            ))}
          </div>
        )}
      </motion.button>

      <p className="mt-3 font-body text-[11px] text-muted-foreground pl-0.5">
        Tap the mic and describe the situation verbally.
      </p>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function AskPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { hasPaidAccess, loading: subscriptionLoading } = useSubscription();
  const { saving, saveClient } = useClientRecords();
  const {
    hasReachedQuestionLimit,
    showOneQuestionRemainingNotice,
    freeQuestionsRemaining,
    dailyMsRemaining,
    incrementFreeQuestion,
    hasUsedFreeClientRecord,
    markFreeClientRecordUsed,
  } = useFreeUsage();

  // Prefill from free-question onboarding (router state) or homepage cinematic intro (sessionStorage seed)
  const seedFromIntro = typeof window !== 'undefined' ? (sessionStorage.getItem('hpe_seed_question') ?? '') : '';
  if (seedFromIntro && typeof window !== 'undefined') sessionStorage.removeItem('hpe_seed_question');
  const routeState = (location.state as { prefill?: string; autoSubmit?: boolean } | null) ?? null;
  const prefillQuestion = (routeState?.prefill ?? '') || seedFromIntro;
  const shouldAutoSubmitPrefill = !!routeState?.autoSubmit && !!prefillQuestion;

  const crFileRef = useRef<HTMLInputElement>(null);
  const drFileRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('intake');
  const [serviceSelection, setServiceSelection] = useState<UserServiceSelection>(null);
  const [questionCount, setQuestionCount] = useState(0);

  const [crImage, setCrImage] = useState<File | null>(null);
  const [crPreview, setCrPreview] = useState<string | null>(null);
  const [drImage, setDrImage] = useState<File | null>(null);
  const [drPreview, setDrPreview] = useState<string | null>(null);
  const [question, setQuestion] = useState(() => prefillQuestion);

  const [plan, setPlan] = useState<HairPlan | null>(null);
  const { isAdmin } = useAdmin();

  const [errorMsg, setErrorMsg] = useState('');
  const [errorKind, setErrorKind] = useState<ErrorKind>('failed');
  const [errorTraceId, setErrorTraceId] = useState<string>('');
  const [traceCopied, setTraceCopied] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showSavePaywall, setShowSavePaywall] = useState(false);
  const [saveClientName, setSaveClientName] = useState('');
  const [saveNotes, setSaveNotes] = useState('');
  const [savedId, setSavedId] = useState<string | null>(null);

  const [primeCanvas, setPrimeCanvas] = useState<PRIMECanvas>({
    porosity: '', resistance: '', integrity: '', melaninBase: '', endGoal: '',
    currentLevel: '', artificialPigment: '', naturalBase: '', visualTexture: '', aim: '', strategy: '',
  });
  const autoSubmitStartedRef = useRef(false);
  // Tracks the currently-active analysis. If the user hits Go Back before the
  // slow answer arrives, we clear this so the late response doesn't yank them
  // out of the intake screen. Retry reuses the same requestId so the server
  // returns the cached answer instantly instead of running the AI again.
  const activeRequestRef = useRef<string | null>(null);
  const [currentRequestId, setCurrentRequestId] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyRefresh, setHistoryRefresh] = useState(0);
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyEmptyMsg, setHistoryEmptyMsg] = useState<string | null>(null);
  const historyLoadTokenRef = useRef(0);

  const loadHistoryItem = useCallback(async (item: HistoryItem) => {
    const token = ++historyLoadTokenRef.current;
    // Clear previous answer IMMEDIATELY so stale content is never shown.
    setPlan(null);
    setErrorMsg('');
    setSavedId(null);
    setHistoryEmptyMsg(null);
    setHistoryLoading(true);
    setSelectedRequestId(item.request_id);
    setCrImage(null); setCrPreview(null);
    setDrImage(null); setDrPreview(null);
    activeRequestRef.current = null;
    setCurrentRequestId(null);
    setStep('results');

    // Persist selection in URL for refresh recovery.
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('c', item.request_id);
      window.history.replaceState({}, '', url.toString());
    } catch { /* noop */ }

    // Always fetch the fresh row so we get the full payload (list rows may be trimmed).
    let raw: string | undefined = item.payload?.result;
    let questionText = item.question_text || '';
    let serviceType = item.service_type ?? null;
    let crPath: string | null = null;
    let drPath: string | null = null;
    try {
      const { data } = await supabase
        .from('analysis_cache')
        .select('request_id, question_text, service_type, payload')
        .eq('request_id', item.request_id)
        .maybeSingle();
      if (data) {
        const p = (data.payload as { result?: string; cr_image_path?: string | null; dr_image_path?: string | null } | null);
        raw = p?.result ?? raw;
        crPath = p?.cr_image_path ?? null;
        drPath = p?.dr_image_path ?? null;
        questionText = data.question_text || questionText;
        serviceType = data.service_type ?? serviceType;
      }
    } catch (e) {
      console.warn('[AskPage] failed to fetch consultation payload', e);
    }

    // If the user clicked another item while we were fetching, abandon this one.
    if (historyLoadTokenRef.current !== token) return;

    // Restore the CR/DR photos from private storage via signed URLs.
    const signPhoto = async (path: string | null): Promise<string | null> => {
      if (!path) return null;
      try {
        const { data, error } = await supabase.storage.from('client-photos').createSignedUrl(path, 3600);
        if (error) { console.warn('[AskPage] signed URL failed', path, error); return null; }
        return data?.signedUrl ?? null;
      } catch (e) { console.warn('[AskPage] signed URL exception', e); return null; }
    };
    const [crUrl, drUrl] = await Promise.all([signPhoto(crPath), signPhoto(drPath)]);
    if (historyLoadTokenRef.current !== token) return;
    if (crUrl) setCrPreview(crUrl);
    if (drUrl) setDrPreview(drUrl);

    setHistoryLoading(false);

    if (!raw) {
      setHistoryEmptyMsg('This consultation does not have a completed answer yet.');
      setQuestion(questionText);
      return;
    }

    const svc: UserServiceSelection =
      serviceType === 'COLOR' ? 'COLOR'
      : serviceType === 'BOTH' ? 'HAIR_RESCUE'
      : serviceType === 'CUTTING' ? 'HAIRCUT'
      : null;
    setServiceSelection(svc);
    setQuestion(questionText);
    const parsed = parseAIResponse(raw, questionText, svc);
    setPlan(parsed);
    setStep('results');
  }, []);

  // Restore selected consultation from ?c=<request_id> on mount / after auth.
  useEffect(() => {
    if (!isAuthenticated) return;
    const params = new URLSearchParams(window.location.search);
    const c = params.get('c');
    if (!c || selectedRequestId) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('analysis_cache')
        .select('request_id, question_text, service_type, payload, created_at')
        .eq('request_id', c)
        .maybeSingle();
      if (cancelled || !data) return;
      loadHistoryItem(data as unknown as HistoryItem);
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, selectedRequestId, loadHistoryItem]);

  useEffect(() => { trackAskPageVisit(); }, []);

  // After the answer renders, the stylist stays on the result until they
  // explicitly tap the "Continue" button below the answer.


  const handleImageSelect = (
    setter: (f: File) => void,
    previewSetter: (s: string) => void
  ) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset the input value so re-selecting the same file/camera shot refires onChange
    e.target.value = '';
    if (!file) return;
    setter(file);
    const reader = new FileReader();
    reader.onload = () => previewSetter(reader.result as string);
    reader.readAsDataURL(file);
  };

  const clearCrImage = () => { setCrImage(null); setCrPreview(null); if (crFileRef.current) crFileRef.current.value = ''; };
  const clearDrImage = () => { setDrImage(null); setDrPreview(null); if (drFileRef.current) drFileRef.current.value = ''; };

  const canSubmit = !!(crImage || drImage || question.trim() || prefillQuestion);

  const handleSubmit = useCallback(async () => {
    console.warn('[AskPage] 1. Ask button clicked', {
      canSubmit,
      hasCrImage: !!crImage,
      hasDrImage: !!drImage,
      questionLen: question.trim().length,
      prefillQuestion: !!prefillQuestion,
      serviceSelection,
      step,
      isAuthenticated,
    });
    console.warn('[AskPage] 2. Validation started');
    if (!canSubmit) {
      console.warn('[AskPage] EARLY RETURN: canSubmit=false (no question, no CR, no DR, no prefill)');
      return;
    }
    console.warn('[AskPage] 3. Validation passed');

    setStep('analyzing');
    setErrorMsg('');
    setErrorKind('failed');
    setTraceCopied(false);

    // A fresh trace_id per submit attempt. Retry generates a new trace so
    // the exact execution is pinpointable, but the underlying requestId
    // stays stable so the answer stays idempotent via analysis_cache.
    const traceId =
      (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
        ? crypto.randomUUID()
        : `t_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    setErrorTraceId(traceId);
    console.warn('[AskPage] trace_id', { traceId });

    const overallStartedAt = Date.now();

    try {
      console.warn('[AskPage] 4. Preparing payload');
      // ── UPLOAD (base64 encode) ───────────────────────────────────────────
      const uploadStartedAt = Date.now();
      let crImageBase64: string | undefined;
      let drImageBase64: string | undefined;
      if (crImage) crImageBase64 = await fileToBase64(crImage);
      if (drImage) drImageBase64 = await fileToBase64(drImage);
      const uploadMs = Date.now() - uploadStartedAt;
      const photoCount = (crImage ? 1 : 0) + (drImage ? 1 : 0);
      console.warn('[AskPage] 5. Photo count', { photoCount, crBytes: crImage?.size ?? 0, drBytes: drImage?.size ?? 0, uploadMs });

      const serviceOverride = serviceSelection === 'HAIRCUT' ? 'CUTTING'
        : serviceSelection === 'COLOR' ? 'COLOR'
        : serviceSelection === 'HAIR_RESCUE' ? 'BOTH'
        : null;

      const effectiveText = question.trim() || prefillQuestion || null;
      if (effectiveText) captureUpgradeIntent(effectiveText);

      const primeFields = Object.entries(primeCanvas).filter(([, v]) => v);
      const primeContext = primeFields.length
        ? `\n\nPRIME CANVAS PRE-FILLED:\n` + primeFields.map(([k, v]) => `- ${k}: ${v}`).join('\n')
        : '';

      const fingerprint = JSON.stringify({
        t: (effectiveText ?? '') + primeContext,
        s: serviceOverride,
        cr: crImageBase64 ? crImageBase64.length : 0,
        dr: drImageBase64 ? drImageBase64.length : 0,
      });
      const requestId = currentRequestId ?? await sha256HexBrowser(fingerprint);
      setCurrentRequestId(requestId);
      activeRequestRef.current = requestId;

      const payload = {
        traceId,
        requestId,
        crImageBase64: crImageBase64 ?? null,
        crMimeType: crImage?.type ?? null,
        drImageBase64: drImageBase64 ?? null,
        drMimeType: drImage?.type ?? null,
        userText: effectiveText ? effectiveText + primeContext : primeContext.trim() || null,
        serviceOverride: serviceOverride ?? null,
        primeCanvas: primeFields.length ? primeCanvas : null,
      };
      const payloadJson = JSON.stringify(payload);
      const payloadBytes = new Blob([payloadJson]).size;
      console.warn('[AskPage] 6. Payload size (bytes)', { payloadBytes, mb: (payloadBytes / 1_048_576).toFixed(2) });

      // Hard cap so we fail loud instead of Chrome silently dropping a >6MB POST
      const MAX_PAYLOAD_BYTES = 5 * 1024 * 1024;
      if (payloadBytes > MAX_PAYLOAD_BYTES) {
        console.error('[AskPage] EARLY RETURN: payload too large', { payloadBytes });
        throw new Error(`Photo is too large (${(payloadBytes / 1_048_576).toFixed(1)}MB). Please retake or use a smaller image.`);
      }

      // Non-destructive watchdog: log only. The real await below decides
      // success vs. error — we must not race it into a false "error" state
      // when the AI legitimately takes 10–20s to respond.
      const softTimeoutId = window.setTimeout(() => {
        if (activeRequestRef.current === requestId) {
          console.warn('[AskPage] Analysis still running past', ANALYSIS_TIMEOUT_MS, 'ms — awaiting response');
        }
      }, ANALYSIS_TIMEOUT_MS);

      try {
        console.warn('[AskPage] 7. About to call analyze-hair');
        const invokeStartedAt = Date.now();
        console.warn('[AskPage] 7a. auth.getSession() entered');
        let { data: { session } } = await supabase.auth.getSession();
        const nowSec = Math.floor(Date.now() / 1000);
        const expiresAt = session?.expires_at ?? 0;
        const shouldRefreshSession = !!session && (!expiresAt || expiresAt - nowSec < 60);
        console.warn('[AskPage] 7b. session state', {
          hasSession: !!session,
          userId: session?.user?.id,
          expiresAt,
          secondsUntilExpiry: expiresAt ? expiresAt - nowSec : null,
          needsRefresh: shouldRefreshSession,
        });
        if (shouldRefreshSession) {
          console.warn('[AskPage] 7c. refreshing session...');
          const { data: refreshed, error: refreshErr } = await supabase.auth.refreshSession();
          if (refreshErr || !refreshed?.session?.access_token) {
            console.error('[AskPage] 7d. refresh failed', refreshErr);
            window.clearTimeout(softTimeoutId);
            activeRequestRef.current = null;
            setErrorMsg('Your session expired. Please sign in again.');
            navigate('/login');
            return;

          }
          session = refreshed.session;
          console.warn('[AskPage] 7e. session refreshed', { userId: session?.user?.id, expiresAt: session?.expires_at });
        } else if (!session) {
          console.warn('[AskPage] 7c. anonymous request: no session refresh needed');
        }
        const accessToken = session?.access_token;
        console.warn('[AskPage] 8. supabase.functions.invoke() entered', { hasAccessToken: !!accessToken });
        const doInvoke = async () => {
          const anonHdrs = await withAnonHeader({});
          return supabase.functions.invoke('analyze-hair', {
            headers: {
              'x-trace-id': traceId,
              ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
              ...anonHdrs,
            },
            body: payload,
          });
        };

        console.warn('[AskPage] 8. supabase.functions.invoke() entered', { hasAccessToken: !!accessToken });
        let invokeResult = await doInvoke();

        // One-time retry when the server rejects our anon token.
        let preReadBody: string | null = null;
        if (invokeResult.error) {
          const ctx0 = (invokeResult.error as { context?: Response }).context;
          if (ctx0?.status === 401 && typeof ctx0.text === 'function') {
            try {
              const bt = await ctx0.text();
              const p = bt ? JSON.parse(bt) : null;
              if (p?.code === 'anon_missing' || p?.code === 'anon_invalid') {
                console.warn('[AskPage] anon token rejected — clearing and retrying', { code: p.code, traceId });
                clearAnonToken();
                invokeResult = await doInvoke();
              } else {
                preReadBody = bt;
              }
            } catch { /* ignore */ }
          }
        }

        const invokeMs = Date.now() - invokeStartedAt;
        window.clearTimeout(softTimeoutId);

        if (activeRequestRef.current !== requestId) {
          console.info('[AskPage] analyze-hair result discarded (user navigated away)', { traceId });
          return;
        }

        const { data, error } = invokeResult;

        if (error) {
          const ctx = (error as { context?: Response }).context;
          let detail = error.message || 'Analysis failed';
          const status: number | undefined = ctx?.status;
          let code: string | undefined;
          let retryable = false;
          let serverTrace: string | undefined = ctx?.headers?.get?.('x-trace-id') ?? undefined;
          const bodyText = preReadBody ?? (ctx && typeof ctx.text === 'function' ? await ctx.text().catch(() => '') : '');
          if (bodyText) {
            try {
              const parsed = JSON.parse(bodyText);
              if (parsed?.error) detail = parsed.error;
              if (parsed?.code) code = parsed.code;
              if (parsed?.retryable) retryable = true;
              if (parsed?.traceId) serverTrace = parsed.traceId;
            } catch {
              detail = bodyText.slice(0, 240);
            }
          }
          if (serverTrace) setErrorTraceId(serverTrace);
          const err2 = new Error(detail) as Error & { status?: number; code?: string; retryable?: boolean };
          err2.status = status;
          err2.code = code;
          err2.retryable = retryable;
          throw err2;
        }
        if (data?.error) {
          if (data?.traceId) setErrorTraceId(data.traceId);
          throw new Error(data.error);
        }

        // Rotate the anon token if the server issued a refresh in the body.
        applyRefreshFromBody(data);


        const rawText = (data?.result ?? data?.answer ?? data?.content ?? data?.text) as string | undefined;
        if (!rawText || typeof rawText !== 'string') {
          console.error('[AskPage] analyze-hair returned without answer text:', data);
          throw new Error('The answer came back in an unexpected format.');
        }
        const parsed = parseAIResponse(rawText, question.trim(), serviceSelection);
        setPlan(parsed);

        // Timing breakdown — logs upload, server DB, server AI, client total

        // so the slowest step is easy to spot in the console.
        const serverTimings = (data as { timings?: Record<string, number | boolean> })?.timings ?? {};
        console.info('[AskPage] analyze-hair TIMINGS', {
          uploadMs,
          invokeMs,
          serverDbMs: serverTimings.db_ms ?? null,
          serverAiMs: serverTimings.ai_ms ?? null,
          serverTotalMs: serverTimings.total_ms ?? null,
          cacheHit: serverTimings.cache_hit ?? false,
          totalMs: Date.now() - overallStartedAt,
        });

        if (!isAuthenticated && !(data as { cached?: boolean })?.cached) {
          incrementFreeQuestion();
        }

        const newCount = questionCount + 1;
        setQuestionCount(newCount);
        if (newCount === 1) trackFirstQuestionSubmitted();
        else if (newCount === 2) trackSecondQuestionSubmitted();

        setSelectedRequestId(requestId);
        setHistoryEmptyMsg(null);
        setHistoryLoading(false);
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('c', requestId);
          window.history.replaceState({}, '', url.toString());
        } catch { /* noop */ }
        setStep('results');
        setHistoryRefresh((n) => n + 1);
        markAnswerSeen();
      } catch (err) {
        window.clearTimeout(softTimeoutId);
        if (activeRequestRef.current !== requestId) return;
        throw err;
      }
    } catch (err) {
      const errAny = err as Error & { status?: number; code?: string; retryable?: boolean };
      const detail = err instanceof Error ? err.message : 'Something interrupted your analysis.';
      let kind: ErrorKind = 'failed';
      if (errAny.code === 'paywall_required' || errAny.status === 402) {
        kind = 'paywall';
      } else if (
        errAny.retryable ||
        errAny.status === 429 ||
        errAny.status === 500 ||
        errAny.status === 502 ||
        errAny.status === 503 ||
        errAny.status === 504 ||
        // Network / FunctionsFetchError (no status) → treat as retryable
        (!errAny.status && !errAny.code)
      ) {
        kind = 'retryable';
      }
      console.error('[AskPage] analyze-hair failed:', {
        traceId,
        detail,
        kind,
        status: errAny.status,
        code: errAny.code,
        elapsedMs: Date.now() - overallStartedAt,
        hasCrImage: !!crImage,
        hasDrImage: !!drImage,
        serviceSelection,
        error: err,
      });
      setErrorMsg(detail);
      setErrorKind(kind);
      setStep('error');
    }
  }, [canSubmit, isAuthenticated, crImage, drImage, serviceSelection, question, prefillQuestion, primeCanvas, incrementFreeQuestion, questionCount, currentRequestId]);

  useEffect(() => {
    if (!shouldAutoSubmitPrefill || autoSubmitStartedRef.current || step !== 'intake') return;
    autoSubmitStartedRef.current = true;
    void handleSubmit();
  }, [shouldAutoSubmitPrefill, step, handleSubmit]);

  const handleSaveClient = async () => {
    if (!plan) return;
    if (!isAuthenticated && hasUsedFreeClientRecord) {
      setShowSavePaywall(true);
      return;
    }
    const colorPlanData = (plan.serviceType === 'color' || plan.serviceType === 'correction')
      ? { ...plan } as unknown as Record<string, unknown> : null;
    const cuttingPlanData = (plan.serviceType === 'cut' || plan.serviceType === 'correction')
      ? { ...plan } as unknown as Record<string, unknown> : null;
    const id = await saveClient({
      clientName: saveClientName.trim() || undefined,
      serviceType: serviceSelection ?? 'COLOR',
      crDescription: question || undefined,
      crFile: crImage,
      drFile: drImage,
      colorPlan: colorPlanData,
      cuttingPlan: cuttingPlanData,
      formula: plan.formula || undefined,
      notes: saveNotes.trim() || undefined,
    });
    if (id) {
      if (!isAuthenticated) markFreeClientRecordUsed();
      setSavedId(id);
      setShowSaveModal(false);
    }
  };

  const reset = () => {
    activeRequestRef.current = null;
    setCurrentRequestId(null);
    setStep('intake');
    clearCrImage(); clearDrImage();
    setQuestion('');
    setPlan(null); setErrorMsg('');
    setServiceSelection(null);
    setSaveClientName(''); setSaveNotes(''); setSavedId(null);
    setPrimeCanvas({ porosity: '', resistance: '', integrity: '', melaninBase: '', endGoal: '', currentLevel: '', artificialPigment: '', naturalBase: '', visualTexture: '', aim: '', strategy: '' });
    setShowSaveModal(false);
    setSelectedRequestId(null);
    setHistoryLoading(false);
    setHistoryEmptyMsg(null);
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('c');
      window.history.replaceState({}, '', url.toString());
    } catch { /* noop */ }
  };

  const handleNewQuestion = () => {
    if (!isAuthenticated && hasReachedQuestionLimit) {
      setErrorMsg('You\u2019ve just used your free in-chair question.\nContinue with Founding Stylist Access for unlimited real-time guidance.');
      setErrorKind('paywall');
      setErrorTraceId('');
      setStep('error');
    } else {
      reset();
    }
  };

  return (
    <>
      <Navigation />
      <SEO title="Ask — Instant Answers Behind the Chair | HairProEncyclopedia.ai" description="Submit your client read and desired result. Get a precise, corrective answer in seconds." path="/ask" />
    <div className="flex w-full">
      {isAuthenticated && (
        <AskHistorySidebar
          onSelect={loadHistoryItem}
          onNewQuestion={reset}
          refreshKey={historyRefresh}
        />
      )}
      {isAuthenticated && (
        <AskHistoryDrawer
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          onSelect={loadHistoryItem}
          onNewQuestion={reset}
          refreshKey={historyRefresh}
        />
      )}
    <PageContainer
      className="app-page-flex !h-[100dvh] px-0 pt-0 pb-0 flex-1 min-w-0"
      style={{ background: 'var(--gradient-page)' } as React.CSSProperties}
    >
      {/* Paywalls */}

{/* ── Sticky Header ─────────────────────────────────────────────────── */}
      <div className="sticky top-0 z-10 flex items-center gap-3 border-b border-border bg-background/90 px-5 py-4 backdrop-blur-md">
        {isAuthenticated ? (
          <motion.button whileTap={{ scale: 0.95 }} onClick={() => setHistoryOpen(true)}
            className="md:hidden flex items-center justify-center h-8 w-8 rounded-full text-muted-foreground transition-colors hover:text-foreground hover:bg-muted"
            aria-label="Your Questions">
            <History className="h-4.5 w-4.5" />
          </motion.button>
        ) : (
          <motion.button whileTap={{ scale: 0.95 }} onClick={() => navigate('/')}
            className="flex items-center justify-center h-8 w-8 rounded-full text-muted-foreground transition-colors hover:text-foreground hover:bg-muted">
            <ArrowLeft className="h-4.5 w-4.5" />
          </motion.button>
        )}

        <div className="flex-1 min-w-0">
          <h1 className="font-display text-lg text-foreground leading-tight">Ask Hair Pro Encyclopedia.ai</h1>
          <p className="font-body text-[11px] text-muted-foreground">Instant Answers Behind the Chair</p>
        </div>

        {/* Free usage indicator */}
        {!isAuthenticated && step === 'intake' && (
          <AnimatePresence mode="wait">
            {hasReachedQuestionLimit ? (
              <motion.button key="upgrade" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                onClick={() => navigate('/pricing')}
                className="rounded-full border border-destructive/40 bg-destructive/8 px-3 py-1.5 font-body text-[11px] font-semibold text-destructive">
                Upgrade
              </motion.button>
            ) : showOneQuestionRemainingNotice ? (
              <motion.span key="one-left" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                className="rounded-full border border-border bg-card px-3 py-1.5 font-body text-[11px] font-medium"
                style={{ color: 'hsl(38 92% 38%)' }}>
                1 question left
              </motion.span>
            ) : (
              <motion.span key="count" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="rounded-full border border-border bg-card px-3 py-1.5 font-body text-[11px] text-muted-foreground">
                {freeQuestionsRemaining} free {freeQuestionsRemaining === 1 ? 'question' : 'questions'}
              </motion.span>
            )}
          </AnimatePresence>
        )}

        {step === 'results' && (
          <span className="font-body text-[11px] text-muted-foreground">
            {plan?.serviceType === 'color' ? 'Color Plan' : plan?.serviceType === 'correction' ? 'Rescue Plan' : 'Cutting Plan'}
          </span>
        )}
      </div>

      {/* ── Master positioning quote ── */}
      <p
        className="mx-auto max-w-md px-5 pt-4 text-center font-body text-[14px] italic leading-relaxed font-medium tracking-[0.01em]"
        style={{
          color: '#111111',
          textShadow:
            '0 0 1px hsl(178 90% 55% / 0.55), 0 0 6px hsl(178 90% 55% / 0.35), 0 0 14px hsl(178 90% 50% / 0.18)',
        }}
      >
        "Like a Master Stylist in your ear- but you stay in control."
      </p>

      <div className="app-page-body">
        <AnimatePresence mode="wait">

          {/* ══ INTAKE ══════════════════════════════════════════════════════ */}
          {step === 'intake' && (
            <motion.div key="intake" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              className="px-5 py-6 pb-28 space-y-6">

              {/* Prefill banner — shown only when a custom question was carried in */}
              {prefillQuestion && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                  className="rounded-2xl border px-4 py-3.5"
                  style={{ borderColor: 'hsl(44 60% 40% / 0.5)', background: 'hsl(44 30% 10% / 0.07)' }}
                >
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Check className="h-3.5 w-3.5 shrink-0" style={{ color: 'hsl(44 65% 50%)' }} />
                    <p className="font-body text-[10px] font-semibold uppercase tracking-widest" style={{ color: 'hsl(44 65% 50%)' }}>
                      Your Free Question
                    </p>
                  </div>
                  <p className="font-body text-sm text-foreground leading-relaxed">"{prefillQuestion}"</p>
                  <p className="mt-2 font-body text-[11px] text-muted-foreground leading-relaxed">
                    Add photos or extra details for a more precise answer.
                  </p>
                </motion.div>
              )}

              {/* Page intro — hidden when prefill is active to avoid redundancy */}
              {!prefillQuestion && (
                <div className="pt-1">
                  <p className="font-body text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1.5">
                    Hair Pro Encyclopedia.ai · CR → DR → Solution
                  </p>
                  <h2 className="font-display text-2xl text-foreground leading-tight">
                    What's happening<br />in the chair?
                  </h2>
                  <p className="mt-2 font-body text-sm text-muted-foreground leading-relaxed">
                    A professional diagnostic tool for licensed stylists. Use photos, text, or voice — Hair Pro Encyclopedia.ai returns the precise plan.
                  </p>
                </div>
              )}

              {/* PRIME Your CANVAS — optional, tap to open */}
              <PRIMEIntakeWidget canvas={primeCanvas} onChange={setPrimeCanvas} />

              {/* Service type */}
              <div>
                <p className="font-body text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-3">
                  Service Type (optional)
                </p>
                <ServiceTypeSelector selected={serviceSelection} onChange={setServiceSelection} />
              </div>

              {/* Soft prompt */}
              <p className="text-center font-display text-base sm:text-lg font-semibold text-foreground">
                What are you trying to achieve?
              </p>

              {/* Divider */}
              <div className="flex items-center gap-3">
                <div className="h-px flex-1 bg-border" />
                <p className="font-body text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                  {prefillQuestion ? 'Add Photos or Details' : 'Describe the Situation'}
                </p>
                <div className="h-px flex-1 bg-border" />
              </div>

              {/* SECTION 1 — CR */}
              <PhotoSection
                sectionNum="1"
                title="Current Reality (CR)"
                uploadLabel="Upload photo of the hair now"
                helperText="Show what the hair currently looks like."
                accent={false}
                preview={crPreview}
                onSelect={handleImageSelect(setCrImage, setCrPreview)}
                onClear={clearCrImage}
                inputRef={crFileRef}
              />

              {/* SECTION 2 — DR */}
              <PhotoSection
                sectionNum="2"
                title="Desired Reality (DR)"
                uploadLabel="Upload inspiration or goal result"
                helperText="Show the target level, tone, or haircut you want to achieve."
                accent={true}
                preview={drPreview}
                onSelect={handleImageSelect(setDrImage, setDrPreview)}
                onClear={clearDrImage}
                inputRef={drFileRef}
              />

              {/* SECTION 3 — Text Input */}
              <div className="rounded-2xl border border-border bg-card p-5">
                <SectionLabel number="3" title="Describe the situation" />
                <textarea
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder={`"My client wants to go from a level 5 to a level 10 blonde but the mids lifted unevenly."`}
                  rows={4}
                  className="w-full resize-none rounded-xl border border-border bg-background px-4 py-3 font-body text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-ring leading-relaxed"
                />
                <p className="mt-2.5 font-body text-[11px] text-muted-foreground pl-0.5">
                  Photos alone are enough — text is optional but adds precision to the plan.
                </p>
              </div>


              {/* SECTION 4 — Voice */}
              <VoiceSection onTranscript={(t) => setQuestion((prev) => prev ? `${prev} ${t}` : t)} />

              {/* SECTION 5 — Submit */}
              <div className="pb-2">
                <motion.button
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  whileHover={canSubmit ? { y: -2, boxShadow: '0 8px 24px -6px hsl(30 10% 12% / 0.25)' } : {}}
                  whileTap={canSubmit ? { scale: 0.98, y: 0 } : {}}
                  className="w-full flex items-center justify-center gap-2.5 rounded-2xl bg-foreground py-4 font-body text-sm font-semibold text-primary-foreground transition-all disabled:opacity-30"
                >
                  <span>
                    {prefillQuestion && !serviceSelection
                      ? 'Show Me the Answer'
                      : serviceSelection === 'HAIRCUT' ? 'Show Me the Answer — Cutting Plan'
                      : serviceSelection === 'COLOR' ? 'Show Me the Answer — Color Plan'
                      : serviceSelection === 'HAIR_RESCUE' ? 'Show Me the Answer — Rescue Plan'
                      : prefillQuestion ? 'Show Me the Answer'
                      : 'Show Me the Answer'}
                  </span>
                  <ChevronRight className="h-4 w-4 opacity-70" />
                </motion.button>
                {canSubmit ? (
                  <p className="mt-2.5 text-center font-body text-[11px] text-muted-foreground/80">
                    Photos alone work — text just makes it even more precise.
                  </p>
                ) : (
                  <p className="mt-2.5 text-center font-body text-[11px] text-muted-foreground/70">
                    Add a photo or describe your situation to begin.
                  </p>
                )}
              </div>
            </motion.div>
          )}

          {/* ══ ANALYZING ═══════════════════════════════════════════════════ */}
          {step === 'analyzing' && (
            <motion.div key="analyzing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="flex flex-col items-center justify-center gap-6 px-5 py-24 min-h-[60vh]">
              {(crPreview || drPreview) && (
                <div className="flex gap-3">
                  {crPreview && (
                    <div className="relative">
                      <img src={crPreview} alt="CR" className="h-20 w-20 rounded-xl object-cover border border-border shadow-sm opacity-60"  loading="lazy" decoding="async"/>
                      <div className="absolute inset-0 rounded-xl ring-2 ring-primary/25 animate-pulse" />
                      <span className="absolute bottom-1.5 left-1.5 rounded-md bg-foreground/70 px-1.5 py-0.5 font-body text-[9px] font-bold text-primary-foreground">CR</span>
                    </div>
                  )}
                  {drPreview && (
                    <div className="relative">
                      <img src={drPreview} alt="DR" className="h-20 w-20 rounded-xl object-cover border border-border shadow-sm opacity-60"  loading="lazy" decoding="async"/>
                      <div className="absolute inset-0 rounded-xl ring-2 ring-primary/15 animate-pulse" style={{ animationDelay: '0.3s' }} />
                      <span className="absolute bottom-1.5 left-1.5 rounded-md bg-foreground/70 px-1.5 py-0.5 font-body text-[9px] font-bold text-primary-foreground">DR</span>
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-center gap-3 rounded-2xl border border-border bg-card px-6 py-3.5 shadow-sm">
                <div className="flex gap-1">
                  {[0, 1, 2].map((i) => (
                    <motion.span key={i} className="block h-2 w-2 rounded-full bg-foreground/50"
                      animate={{ scale: [1, 1.5, 1], opacity: [0.4, 1, 0.4] }}
                      transition={{ duration: 0.9, repeat: Infinity, delay: i * 0.2 }} />
                  ))}
                </div>
                <span className="font-body text-sm text-foreground">{serviceSelection === 'HAIRCUT' ? 'Creating your custom cutting plan…' : serviceSelection === 'COLOR' ? 'Building your custom color plan…' : 'Hair Pro Encyclopedia.ai is analyzing the situation…'}</span>
              </div>

              <p className="font-body text-xs text-muted-foreground text-center max-w-[240px] leading-relaxed">
                Detecting service type and building a precise correction path.
              </p>
            </motion.div>
          )}

          {/* ══ RESULTS ═════════════════════════════════════════════════════ */}
          {step === 'results' && historyLoading && (
            <motion.div key={`loading-${selectedRequestId}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              className="flex flex-col items-center justify-center gap-3 px-5 py-24 min-h-[60vh]">
              <div className="flex gap-1">
                {[0, 1, 2].map((i) => (
                  <motion.span key={i} className="block h-2 w-2 rounded-full bg-foreground/50"
                    animate={{ scale: [1, 1.5, 1], opacity: [0.4, 1, 0.4] }}
                    transition={{ duration: 0.9, repeat: Infinity, delay: i * 0.2 }} />
                ))}
              </div>
              <p className="font-body text-sm text-foreground">Loading consultation…</p>
            </motion.div>
          )}

          {step === 'results' && !historyLoading && historyEmptyMsg && (
            <motion.div key={`empty-${selectedRequestId}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              className="flex flex-col items-center justify-center gap-3 px-5 py-24 min-h-[60vh] text-center">
              <p className="font-body text-sm text-muted-foreground max-w-xs">{historyEmptyMsg}</p>
            </motion.div>
          )}

          {step === 'results' && !historyLoading && !historyEmptyMsg && plan && (
            <motion.div key={`results-${selectedRequestId ?? 'new'}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              className="px-5 py-6 pb-28 space-y-5">

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35 }}
                className="text-center space-y-2"
              >
                <h2 className="font-display text-3xl sm:text-4xl text-foreground leading-tight">
                  Here’s Your Hair Pro Guidance
                </h2>
                <p className="mx-auto max-w-lg font-body text-sm text-muted-foreground leading-relaxed">
                  Review your answer first. When you’re ready, continue into the deeper Hair Pro Encyclopedia.ai experience.
                </p>
              </motion.div>

              {/* Photo thumbnails */}
              {(crPreview || drPreview) && (
                <div className="flex gap-3">
                  {crPreview && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="flex-1">
                      <p className="font-body text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1.5">Current Reality</p>
                      <img src={crPreview} alt="CR" className="w-full max-h-40 rounded-xl object-cover border border-border shadow-sm"  loading="lazy" decoding="async"/>
                    </motion.div>
                  )}
                  {drPreview && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }} className="flex-1">
                      <p className="font-body text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1.5">Desired Reality</p>
                      <img src={drPreview} alt="DR" className="w-full max-h-40 rounded-xl object-cover border border-border shadow-sm"  loading="lazy" decoding="async"/>
                    </motion.div>
                  )}
                </div>
              )}

              {/* Plan card — wrapped in glassmorphism card */}
              <div className="card-glass">
                <PlanCard plan={plan} question={question.trim() || prefillQuestion || undefined} />
              </div>

              {(selectedRequestId || currentRequestId) && (
                <CaseConversation
                  requestId={(selectedRequestId || currentRequestId) as string}
                  crImage={crImage}
                  drImage={drImage}
                  onNewQuestion={handleNewQuestion}
                />
              )}


              {/* Post-answer Go Pro CTA (hidden for signed-in stylists) */}
              {!isAuthenticated && !subscriptionLoading && !hasPaidAccess && !isAdmin && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="rounded-2xl border border-primary/40 bg-gradient-to-br from-primary/10 via-card to-card px-6 py-6 text-center shadow-sm"
                >
                  <p className="font-display text-lg text-foreground font-semibold">
                    Ready for instant answers behind the chair?
                  </p>
                  <motion.button
                    whileHover={{ y: -1 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => navigate('/pricing')}
                    className="mt-4 inline-flex items-center justify-center rounded-xl bg-primary px-8 py-3 font-body text-sm font-semibold text-primary-foreground shadow-md transition-opacity hover:opacity-90"
                  >
                    Go Pro
                  </motion.button>
                </motion.div>
              )}

              {!isAuthenticated && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 }}
                  className="space-y-2 text-center"
                >
                  <p className="font-display text-[11px] uppercase tracking-[0.32em] text-primary/80">
                    From Panic to Precision
                  </p>
                  <motion.button
                    whileHover={{ y: -2, boxShadow: '0 12px 32px -8px hsl(var(--primary) / 0.45)' }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => navigate('/stylist')}
                    className="w-full flex items-center justify-center gap-2.5 rounded-2xl bg-primary py-4 font-body text-base font-semibold text-primary-foreground shadow-md transition-all"
                  >
                    Continue
                    <ChevronRight className="h-4 w-4 opacity-80" />
                  </motion.button>
                </motion.div>
              )}

              {!isAuthenticated && <AnswerFeedback />}

              {/* Save Client — hidden on signed-in workspace to keep answer focused */}

              {!isAuthenticated && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
                  {savedId ? (
                    <div className="flex items-center gap-2 rounded-2xl border border-primary/30 bg-primary/5 px-5 py-3.5">
                      <Check className="h-4 w-4 text-primary shrink-0" />
                      <span className="font-body text-sm text-primary">Client saved to records</span>
                      <button onClick={() => navigate('/client-records')}
                        className="ml-auto font-body text-xs text-primary underline-offset-2 hover:underline">
                        View records
                      </button>
                    </div>
                  ) : (
                    <motion.button
                      whileHover={{ y: -1 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setShowSaveModal(true)}
                      className="w-full flex items-center justify-center gap-2.5 rounded-2xl border border-border bg-card px-5 py-3.5 font-body text-sm font-medium text-foreground shadow-sm transition-colors hover:border-primary/40 hover:bg-accent/20">
                      <BookmarkPlus className="h-4 w-4 text-muted-foreground" />
                      Save This Client
                      {!hasUsedFreeClientRecord && (
                        <span className="ml-auto rounded-full bg-muted px-2.5 py-0.5 font-body text-[10px] text-muted-foreground">Free</span>
                      )}
                    </motion.button>
                  )}
                </motion.div>
              )}

              {/* Ask Another — only for anonymous users; signed-in stylists use sidebar "New Question" */}
              {!isAuthenticated && !hasReachedQuestionLimit && (
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="pt-2"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="h-px flex-1 bg-border" />
                    <span className="font-body text-[10px] uppercase tracking-widest text-muted-foreground">Done reviewing?</span>
                    <div className="h-px flex-1 bg-border" />
                  </div>
                  <motion.button
                    whileHover={{ y: -2, boxShadow: '0 8px 24px -6px hsl(30 10% 12% / 0.2)' }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleNewQuestion}
                    className="w-full flex items-center justify-center gap-2.5 rounded-2xl border border-foreground/20 bg-foreground/[0.06] py-4 font-body text-sm font-semibold text-foreground transition-all hover:bg-foreground/10"
                  >
                    <MessageCircle className="h-4 w-4 opacity-60" />
                    Ask Another Question
                  </motion.button>
                </motion.div>
              )}
            </motion.div>
          )}

          {/* ══ ERROR ════════════════════════════════════════════════════════ */}
          {step === 'error' && (
            <motion.div key="error" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="flex flex-col items-center gap-4 px-5 py-16 text-center min-h-[60vh] justify-center">

              {/* ─── PAYWALL 402 ─────────────────────────────────────────────── */}
              {errorKind === 'paywall' && (
                <>
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-500/10 border border-amber-500/30">
                    <Sparkles className="h-6 w-6 text-amber-500" />
                  </div>
                  <div className="space-y-2 max-w-sm">
                    <p className="font-display text-lg text-foreground">Founding Stylist Access</p>
                    <p className="font-body text-sm font-medium text-foreground whitespace-pre-line">
                      {errorMsg || "You've just used your free in-chair question. Continue with Founding Stylist Access for unlimited real-time guidance."}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 pt-1">
                    <motion.button whileHover={{ y: -1 }} onClick={() => { activeRequestRef.current = null; setCurrentRequestId(null); setStep('intake'); }}
                      className="rounded-xl border border-border bg-background px-6 py-3 font-body text-sm font-medium text-foreground transition-opacity hover:opacity-85">
                      Go Back
                    </motion.button>
                    <motion.button whileHover={{ y: -1 }} onClick={() => navigate('/pricing')}
                      className="rounded-xl bg-amber-500 px-8 py-3 font-body text-sm font-semibold text-black transition-opacity hover:opacity-90">
                      Unlock Access
                    </motion.button>
                  </div>
                </>
              )}

              {/* ─── RETRYABLE FAILURE ───────────────────────────────────────── */}
              {errorKind === 'retryable' && (
                <>
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-yellow-500/10 border border-yellow-500/30">
                    <AlertCircle className="h-6 w-6 text-yellow-500" />
                  </div>
                  <div className="space-y-1 max-w-sm">
                    <p className="font-display text-base text-foreground">Something got in the way</p>
                    <p className="font-body text-sm font-medium text-foreground whitespace-pre-line">{errorMsg || 'The connection dropped before your answer came back.'}</p>
                    <p className="font-body text-xs text-muted-foreground leading-relaxed">
                      Your question and photos are saved. Retry won't consume your free question.
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <motion.button whileHover={{ y: -1 }} onClick={() => { activeRequestRef.current = null; setCurrentRequestId(null); setStep('intake'); }}
                      className="rounded-xl border border-border bg-background px-6 py-3 font-body text-sm font-medium text-foreground transition-opacity hover:opacity-85">
                      Go Back
                    </motion.button>
                    <motion.button whileHover={{ y: -1 }} onClick={handleSubmit}
                      className="rounded-xl bg-foreground px-8 py-3 font-body text-sm font-medium text-primary-foreground transition-opacity hover:opacity-85">
                      Retry
                    </motion.button>
                  </div>
                </>
              )}

              {/* ─── ANALYSIS FAILED (non-retryable) ─────────────────────────── */}
              {errorKind === 'failed' && (
                <>
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10 border border-destructive/30">
                    <X className="h-6 w-6 text-destructive" />
                  </div>
                  <div className="space-y-1 max-w-sm">
                    <p className="font-display text-base text-foreground">Analysis failed</p>
                    <p className="font-body text-sm font-medium text-foreground whitespace-pre-line">{errorMsg || 'This request could not be completed.'}</p>
                    <p className="font-body text-xs text-muted-foreground leading-relaxed">
                      Share the trace ID below with support and we'll pinpoint the exact execution.
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <motion.button whileHover={{ y: -1 }} onClick={() => { activeRequestRef.current = null; setCurrentRequestId(null); setStep('intake'); }}
                      className="rounded-xl bg-foreground px-8 py-3 font-body text-sm font-medium text-primary-foreground transition-opacity hover:opacity-85">
                      Start Over
                    </motion.button>
                  </div>
                </>
              )}

              {/* ─── COPYABLE TRACE ID ───────────────────────────────────────── */}
              {errorTraceId && (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(errorTraceId);
                      setTraceCopied(true);
                      window.setTimeout(() => setTraceCopied(false), 1800);
                    } catch { /* noop */ }
                  }}
                  className="mt-4 inline-flex items-center gap-2 rounded-full border border-border bg-background/60 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="Copy trace id"
                >
                  <span>trace</span>
                  <span className="font-mono text-foreground/80 normal-case tracking-normal">{errorTraceId}</span>
                  {traceCopied ? <Check className="h-3 w-3 text-emerald-500" /> : <span className="opacity-60">copy</span>}
                </button>
              )}
            </motion.div>
          )}

        </AnimatePresence>
      </div>

      {/* ── Save Client Bottom Sheet ───────────────────────────────────────── */}
      <AnimatePresence>
        {showSaveModal && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setShowSaveModal(false)}
              className="fixed inset-0 z-40 bg-foreground/25 backdrop-blur-sm" />
            <motion.div
              initial={{ opacity: 0, y: 60 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 60 }}
              transition={{ type: 'spring', damping: 28, stiffness: 320 }}
              className="fixed bottom-0 left-0 right-0 z-50 rounded-t-3xl bg-background border-t border-border px-5 pt-4 pb-10 space-y-4 shadow-2xl">
              <div className="flex justify-center mb-2">
                <div className="h-1 w-10 rounded-full bg-border" />
              </div>
              <div>
                <p className="font-display text-lg text-foreground">Save This Client</p>
                <p className="font-body text-xs text-muted-foreground mt-0.5">
                  {!isAuthenticated && !hasUsedFreeClientRecord
                    ? 'Your free client record slot — store this plan for future visits.'
                    : 'Store this plan in your salon memory for repeat appointments.'}
                </p>
              </div>
              <div className="space-y-1.5">
                <label className="font-body text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Client Name (optional)</label>
                <input value={saveClientName} onChange={(e) => setSaveClientName(e.target.value)}
                  placeholder="e.g. Jessica M."
                  className="w-full rounded-xl border border-border bg-background px-4 py-3 font-body text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
              </div>
              <div className="space-y-1.5">
                <label className="font-body text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Notes (optional)</label>
                <textarea value={saveNotes} onChange={(e) => setSaveNotes(e.target.value)}
                  placeholder="Any additional notes about this client or session…"
                  rows={2}
                  className="w-full resize-none rounded-xl border border-border bg-background px-4 py-3 font-body text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
              </div>
              <motion.button whileHover={{ y: -1 }} onClick={handleSaveClient} disabled={saving}
                className="w-full rounded-2xl bg-foreground py-4 font-body text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-85 disabled:opacity-50">
                {saving ? 'Saving…' : 'Save Client Record'}
              </motion.button>
            </motion.div>
          </>
        )}
      </AnimatePresence>

    </PageContainer>
    </div>
    </>
  );
}
